import { Text } from '@revelio/ui';

import type { ColumnItem } from './card-list-column';
import styles from './card-list-select-controller.module.css';
import {
  ColumnListItemGrid,
  type ColumnListItemGridProps,
} from './column-list-item-grid';

export type ColumnSet<CardType> = {
  heading: string | null;
  columns: ColumnItem<CardType>[];
  maxColumnsAllowed?: number;
  minColumnsRequired?: number;
  supportsCustomColumns?: boolean;
};

export type CardListSelectControllerProps<CardType extends string> = {
  value: CardType[];
} & Pick<
  ColumnListItemGridProps<CardType>,
  | 'hideCtaIfNotSelected'
  | 'hideCheckbox'
  | 'variant'
  | 'separator'
  | 'onCloseSubMenu'
  | 'gridProps'
> & {
    items: ColumnSet<CardType>[];
    onChange?: (value: CardType[]) => void;
    ['data-testid']?: string;
  };

export function CardListSelectController<CardType extends string>(
  props: CardListSelectControllerProps<CardType>
) {
  const {
    items,
    hideCtaIfNotSelected = false,
    hideCheckbox = false,
    gridProps,
    variant,
    separator,
    onCloseSubMenu,
    'data-testid': data_TestId,
    onChange,
    value,
  } = props;

  const checkboxGroupProps = {
    value,
    onChange: onChange && ((next: string[]) => onChange(next as CardType[])),
  };

  return (
    <div data-testid={data_TestId}>
      {items.map((ds, i) => {
        const columnGridItem = (
          <ColumnListItemGrid
            gridProps={gridProps}
            columnItems={ds.columns}
            checkboxGroupProps={checkboxGroupProps}
            variant={variant}
            hideCtaIfNotSelected={hideCtaIfNotSelected}
            hideCheckbox={hideCheckbox}
            onCloseSubMenu={onCloseSubMenu}
            separator={separator}
          />
        );
        if (variant === 'columns' && ds.heading) {
          return (
            <fieldset key={i} className={styles['columnFieldSet']}>
              <legend className={styles['columnFieldSetHeading']}>
                {ds.heading ? (
                  <Text
                    as="h2"
                    fontSize="sm"
                    weight="semibold"
                    className={styles['columnFieldSetHeadingText']}
                  >
                    {ds.heading}
                  </Text>
                ) : null}
              </legend>
              <div>{columnGridItem}</div>
            </fieldset>
          );
        }

        return (
          <div className={styles.section} key={i}>
            {ds.heading ? (
              <Text
                as="h3"
                fontSize="md"
                weight="semibold"
                className={styles['sectionHeading']}
              >
                {ds.heading}
              </Text>
            ) : null}
            {columnGridItem}
          </div>
        );
      })}
    </div>
  );
}
